import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { timer } from 'rxjs';

declare const IcarSDK: any;

export enum WebMitekError {
  NO_CAMERA_DEVICES    = 1, // IcarSDK.RESULT_VIDEO.NO_CAMERA_DEVICES
  NO_CAMERA_PERMISSION = 2, // IcarSDK.RESULT_VIDEO.NO_CAMERA_PERMISSION
  OK                   = 0, // IcarSDK.RESULT_VIDEO.OK
  UNAVAILABLE_CAMERA   = 3, // IcarSDK.RESULT_VIDEO.UNAVAILABLE_CAMERA
  UNKNOWN_ERROR        = 4, // IcarSDK.RESULT_VIDEO.UNSUPPORTED_BROWSER
  UNSUPPORTED_BROWSER  = 5  // IcarSDK.RESULT_VIDEO.UNKNOWN_ERROR
}

enum WebMitekResult {
  FAIL              = 1, // IcarSDK.ResultProcess.FAIL
  ATTEMPTS_EXCEEDED = 2, // IcarSDK.ResultProcess.ATTEMPTS_EXCEEDED
  OK                = 0  // IcarSDK.ResultProcess.OK
}

@Component({
  selector: 'web-mitek-capture',
  templateUrl: './web-mitek-capture.component.html',
  styleUrls: ['./web-mitek-capture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebMitekCaptureComponent implements OnInit {
  showManualTrigger = false;
  videoSize = {
    width: 0,
    height: 0
  };
  @ViewChild('videoPlayer', { static: true }) videoRef: ElementRef;

  @Output() capture = new EventEmitter<string>();
  @Output() error = new EventEmitter<WebMitekError>();

  private static icarSDKLoaded = false;

  constructor(private cdr: ChangeDetectorRef, private renderer: Renderer2) {}

  get videoPlayer() {
    return this.videoRef.nativeElement;
  }

  ngOnInit() {
    if (!WebMitekCaptureComponent.icarSDKLoaded) {
      WebMitekCaptureComponent.icarSDKLoaded = true;
      const script = this.renderer.createElement('script') as HTMLScriptElement;
      script.type = 'text/javascript';
      script.src = 'assets/scripts/icarSDK.js';
      this.renderer.appendChild(document.body, script);
      script.onload = _ => this.initializeIcarSDK();
    } else {
      this.initializeIcarSDK();
    }
  }

  private initializeIcarSDK() {
    this.videoSize = {
      height: Math.min(window.screen.height, 480),
      width: Math.min(window.screen.width, 640)
    };

    const OPTIONS_VIDEO = {
      modeCapture: IcarSDK.MODE_CAPTURE.DOCUMENT_IDCARD,
      callBackFeedBackFunction: e => this.onError(e)
    };

    const OPTIONS_DOCUMENTS = {
      type_document: IcarSDK.TYPE_DOC.IDCARD,
      MAX_ATTEMPTS: -1,
      fontText: 'bold 50px Arial',
      colorText: '#c91f28',
      multiplicationFactorBlurring: 1.0,
      MSG_PLACE_DOC_INSIDE: 'Encuadre el documento',
      MSG_DOC_OK: 'Un momento por favor...',
      MSG_DARK_IMAGE: 'Imagen demasiado oscura',
      MSG_BLURRED_IMAGE: 'Imagen demsiado borrosa',
      MSG_TRY_AGAIN: 'Inténtelo otra vez, acercándose al documento'
    };

    IcarSDK.video.initialize(this.videoPlayer, OPTIONS_VIDEO);

    IcarSDK.documentCapture.create(
      this.videoPlayer,
      onFrameReceivedCallback => {
        try {
          return IcarSDK.video.requestFrame(onFrameReceivedCallback);
        } catch (error) {
          return null;
        }
      },
      (result, modeCapture, imageData) => this.onResult(result, imageData),
      OPTIONS_DOCUMENTS
    );

    timer(600).subscribe(() => {
      IcarSDK.documentCapture.start();
    });

    timer(10000).subscribe(() => {
      // If user isn´t capable to capture the document in 10 seconds, we allow to manually capture
      // the document
      this.enableManualTrigger();
    });
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.stop();
  }

  onManualTrigger() {
    IcarSDK.documentCapture.manualTrigger();
  }

  private onResult(result: WebMitekResult, imageData: ImageData) {
    if (result === IcarSDK.ResultProcess.OK) {
      this.onSuccess(imageData);
    } else {
      this.onError(WebMitekError.UNKNOWN_ERROR);
    }
  }

  private onSuccess(imageData: ImageData) {
    const base64 = this.proccessImage(imageData);
    this.capture.next(base64);
  }

  private onError(error: WebMitekError) {
    if (error === IcarSDK.ResultProcess.OK) {
      return;
    }
    this.error.next(error);
  }

  private proccessImage(image: ImageData) {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d').putImageData(image, 0, 0);

    return canvas.toDataURL('image/jpeg');
  }

  private enableManualTrigger() {
    this.showManualTrigger = true;
    this.cdr.markForCheck();
  }

  private stop() {
    IcarSDK.documentCapture.stop();
    IcarSDK.video.cleanUp();
  }
}
